import React, { useState, useEffect, ChangeEvent, useRef } from "react";
import "./component.css";
import { DELETE_RESUME, PATCH_USER_INFO } from "../utils/constants";
import { makeAuthenticatedDelete, makeAuthenticatedPatchForFile } from "../utils/fetchUtils";
import { useNavigate } from "react-router-dom";
import useWindowWidth from "../utils/windowWidth";
import { IUser } from "../utils/types";
import { Modal } from "react-bootstrap";

// Define the type for form data
type FormDataType = {
  profileInformation: string;
  name: string;
  phoneNumber: string;
  email: string;
  resume: File | null; // Allow File or null
  social: string[];
};

const normalizeSocialLinks = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      if (typeof item !== "string") return [];

      const trimmedItem = item.trim();
      try {
        const parsedItem = JSON.parse(trimmedItem);
        if (Array.isArray(parsedItem)) return normalizeSocialLinks(parsedItem);
      } catch {
        // Keep regular URLs and empty positional values as strings.
      }

      return [trimmedItem];
    });
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) return [];

    try {
      const parsedValue = JSON.parse(trimmedValue);
      if (Array.isArray(parsedValue)) return normalizeSocialLinks(parsedValue);
    } catch {
      // A regular URL is already a valid social value.
    }

    return [trimmedValue];
  }

  return [];
};

const prepareSocialLinksForSubmit = (links: string[]): string[] => {
  const normalizedLinks = links.map((link) => link.trim());

  while (
    normalizedLinks.length > 0 &&
    normalizedLinks[normalizedLinks.length - 1] === ""
  ) {
    normalizedLinks.pop();
  }

  return normalizedLinks;
};

interface EditProfileFormProps {
  userDetails: IUser | null;
}

const getSavedResumeUrl = (resume: unknown): string => {
  if (typeof resume === "string") return resume.trim();
  if (Array.isArray(resume)) {
    const savedResume = resume.find((item) => typeof item === "string" && item.trim());
    return typeof savedResume === "string" ? savedResume.trim() : "";
  }
  return "";
};

const getResumeFileName = (resumeUrl: string): string => {
  if (!resumeUrl) return "resume.pdf";

  try {
    const pathSegments = new URL(resumeUrl).pathname.split("/").filter(Boolean);
    return decodeURIComponent(pathSegments[pathSegments.length - 1] || "resume.pdf");
  } catch {
    const urlWithoutQuery = resumeUrl.split("?")[0];
    return decodeURIComponent(urlWithoutQuery.split("/").pop() || "resume.pdf");
  }
};

const EditProfileForm = ({ userDetails }: EditProfileFormProps) => {
  const navigate = useNavigate();
  const resumeInputRef = useRef<HTMLInputElement>(null);
  const resumeHeadingRef = useRef<HTMLHeadingElement>(null);
  const focusResumeAfterDeleteRef = useRef(false);

  // react states
  const [formData, setFormData] = useState<FormDataType>({
    profileInformation: "",
    name: "",
    phoneNumber: "",
    email: "",
    resume: null,
    social: ["", ""], // Default with two social links
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showDeleteResumeConfirmation, setShowDeleteResumeConfirmation] = useState(false);
  const [isDeletingResume, setIsDeletingResume] = useState(false);
  const [deleteResumeError, setDeleteResumeError] = useState("");
  const [deleteResumeStatus, setDeleteResumeStatus] = useState("");
  const [savedResumeDeleted, setSavedResumeDeleted] = useState(false);
  const savedResumeUrl = getSavedResumeUrl(userDetails?.resume);
  const savedResumeFileName = getResumeFileName(savedResumeUrl);
  const hasSavedResume = Boolean(savedResumeUrl) && !savedResumeDeleted;

  // const handleOptionChange = (event: any) => {
  //   setSelectedOption(event.target.value);
  //   handleChange(event);
  // };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle resume file input
  const handleResumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file && file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      e.target.value = "";
      setSubmitError("Please choose a PDF resume.");
      setFormData((prev) => ({ ...prev, resume: null }));
      return;
    }

    setSubmitError("");
    setFormData((prev) => ({
      ...prev,
      resume: file,
    }));
  };

  const clearSelectedResume = () => {
    setSubmitError("");
    setFormData((prev) => ({ ...prev, resume: null }));
    if (resumeInputRef.current) resumeInputRef.current.value = "";
  };

  const formatFileSize = (size: number) => {
    if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const openDeleteResumeConfirmation = () => {
    setDeleteResumeError("");
    setShowDeleteResumeConfirmation(true);
  };

  const closeDeleteResumeConfirmation = () => {
    if (isDeletingResume) return;
    setShowDeleteResumeConfirmation(false);
    setDeleteResumeError("");
  };

  const deleteSavedResume = async () => {
    if (isDeletingResume) return;

    try {
      setIsDeletingResume(true);
      setDeleteResumeError("");
      const response = await makeAuthenticatedDelete(DELETE_RESUME);
      if (response?.success !== true) {
        throw new Error(response?.message || "Resume deletion failed.");
      }

      setSavedResumeDeleted(true);
      setShowDeleteResumeConfirmation(false);
      setDeleteResumeStatus(
        response?.data?.deleted === false
          ? "No saved resume was found. Your profile is already clear."
          : "Your saved resume and its Career Discovery profile data were deleted."
      );
      localStorage.setItem("resume", "false");
      focusResumeAfterDeleteRef.current = true;
    } catch (error) {
      console.error("Unable to delete resume:", error);
      setDeleteResumeError("We couldn’t delete your resume. Please try again.");
    } finally {
      setIsDeletingResume(false);
    }
  };

  const handleDeleteConfirmationExited = () => {
    if (!focusResumeAfterDeleteRef.current) return;
    focusResumeAfterDeleteRef.current = false;
    resumeHeadingRef.current?.focus();
  };

  // Handle input change for individual fields
  const handleSocialChange = (index: any, value: any) => {
    const updatedSocial = [...formData.social];
    updatedSocial[index] = value;
    setFormData({ ...formData, social: updatedSocial });
  };

  // Add a new input field
  const addSocialInput = () => {
    if (formData.social.length >= 3) {
      alert("You can add a maximum of 3 social links.");
      return;
    }
    setFormData({ ...formData, social: [...formData.social, ""] });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting || !userDetails) return;

    const formDataToSubmit = new FormData();
    formDataToSubmit.append("profileInfo", formData.profileInformation);
    formDataToSubmit.append("userName", formData.name);
    formDataToSubmit.append("phoneNumber", formData.phoneNumber);
    formDataToSubmit.append("email", formData.email);
    if (formData.resume) {
      formDataToSubmit.append("file", formData.resume);
    }
    // Do not persist empty placeholder inputs as `["",""]`.
    formDataToSubmit.append(
      "social",
      JSON.stringify(prepareSocialLinksForSubmit(formData.social))
    );

    try {
      setSubmitError("");
      setIsSubmitting(true);
      const response = await makeAuthenticatedPatchForFile(PATCH_USER_INFO, formDataToSubmit);
      if (response?.success === false) {
        throw new Error(response.message || "Profile update failed.");
      }

      if (formData.resume || hasSavedResume) localStorage.setItem("resume", "true");
      navigate("/profile", {
        state: {
          profileUpdateMessage: formData.resume
            ? "Your profile and resume were updated successfully."
            : "Your profile was updated successfully.",
        },
      });
    } catch (error) {
      console.error("Unable to update profile:", error);
      setSubmitError(
        formData.resume
          ? "We couldn’t upload your resume or update your profile. Please try again."
          : "We couldn’t update your profile. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  //use Effects

  useEffect(() => {
    if (userDetails) {
      const savedSocialLinks = normalizeSocialLinks(userDetails.social);
      const socialInputs = [...savedSocialLinks.slice(0, 3)];
      while (socialInputs.length < 2) socialInputs.push("");

      setFormData((currentFormData) => ({
        ...currentFormData,
        profileInformation: userDetails.profileInfo ?? "",
        name: userDetails.userName ?? "",
        phoneNumber: userDetails.phoneNumber ?? "",
        email: userDetails.email ?? "",
        social: socialInputs,
      }));
      setSavedResumeDeleted(false);
    }
  }, [userDetails]);

  //window size
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 769;

  return (
    <>
      <form
        className="doc-form profile-form"
        onSubmit={handleSubmit}
        aria-busy={isSubmitting}
        id="myForm"
      >
        {isSubmitting && (
          <div
            className="profile-update-status"
            id="profile-update-status"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <span className="resume-parsing-spinner" aria-hidden="true" />
            <div>
              <h2>{formData.resume ? "Uploading your resume" : "Updating your profile"}</h2>
              <p>
                {formData.resume
                  ? "Please keep this page open while your replacement resume and profile changes are saved."
                  : "Please keep this page open while your changes are saved."}
              </p>
            </div>
          </div>
        )}
        <div
          className="input-container"
          style={{ width: isMobile ? "90%" : "46%" }}
        >
          <div className="mb-3 input-div input-div-10">
            <h2 id="profile-info-label" className="form-label">
              Profile Information
            </h2>
            <textarea
              style={{ marginTop: "1rem" }}
              className="form-control input-5"
              id="profile-info"
              name="profileInformation"
              aria-label="profile information"
              value={formData.profileInformation}
              aria-labelledby="profile-info-label"
              placeholder="Write a short para here..."
              rows={7}
              onChange={handleChange}
            ></textarea>
          </div>
        </div>
        <div
          className="input-container"
          style={{ width: isMobile ? "90%" : "46%" }}
        >
          <div className="mb-3 input-div input-div-10">
            <h2 id="full-name-label" className="form-label">
              Full name
            </h2>
            <input
              style={{ marginTop: "1rem" }}
              className="form-control input-5"
              id="full-name"
              name="name"
              aria-label="name"
              value={formData.name}
              aria-labelledby="full-name-label"
              placeholder="your name"
              onChange={handleChange}
            ></input>
          </div>
        </div>
        <div
          className="input-container"
          style={{ width: isMobile ? "90%" : "46%" }}
        >
          <div className="mb-3 input-div input-div-10">
            <h2 id="mobile-label" className="form-label">
              Mobile
            </h2>
            <input
              style={{ marginTop: "1rem" }}
              className="form-control input-5"
              id="mobile"
              name="phoneNumber"
              value={formData.phoneNumber}
              aria-labelledby="mobile-label"
              placeholder="phone number"
              onChange={handleChange}
            ></input>
          </div>
        </div>
        <div
          className="input-container"
          style={{ width: isMobile ? "90%" : "46%" }}
        >
          <div className="mb-3 input-div input-div-10">
            <h2 id="resume-label" className="form-label" ref={resumeHeadingRef} tabIndex={-1}>Resume</h2>
            <div className={`profile-resume-card ${formData.resume ? "has-selection" : hasSavedResume ? "has-resume" : ""}`}>
              <div
                className="profile-resume-file-details"
              >
                <div
                  id="resume-file-state"
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <p className="profile-resume-file-name">
                    {formData.resume
                      ? formData.resume.name
                      : hasSavedResume
                        ? savedResumeFileName
                        : "No resume added"}
                  </p>
                  {(formData.resume || !hasSavedResume) && (
                    <p className="profile-resume-file-meta">
                      {formData.resume
                        ? `PDF, ${formatFileSize(formData.resume.size)}. Ready to upload.`
                        : "Add a PDF to use it with Career Discovery."}
                    </p>
                  )}
                </div>
                {!formData.resume && hasSavedResume && (
                  <div className="profile-resume-card-actions">
                    <a className="profile-resume-download" href={savedResumeUrl} download>
                      Download
                    </a>
                    <button
                      type="button"
                      className="profile-resume-delete"
                      onClick={openDeleteResumeConfirmation}
                      disabled={isSubmitting}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
              {formData.resume ? (
                <button
                  type="button"
                  className="profile-resume-remove"
                  onClick={clearSelectedResume}
                  disabled={isSubmitting}
                >
                  Remove
                </button>
              ) : null}
            </div>
            <input
              className="visually-hidden profile-resume-file-input"
              id="resume"
              ref={resumeInputRef}
              type="file"
              name="resume"
              onChange={handleResumeChange}
              aria-labelledby="resume-label resume-picker-label"
              aria-describedby="resume-file-help resume-file-state"
              accept="application/pdf,.pdf"
              disabled={isSubmitting || !userDetails}
            />
            <label id="resume-picker-label" className="profile-resume-picker" htmlFor="resume">
              {formData.resume
                ? "Choose a different PDF"
                : hasSavedResume
                  ? "Replace resume"
                  : "Add PDF resume"}
            </label>
            <p id="resume-file-help" className="profile-resume-help">
              PDF only. Your selection is uploaded when you save the profile.
            </p>
            {deleteResumeStatus && (
              <p className="profile-resume-delete-status" role="status" aria-live="polite">
                {deleteResumeStatus}
              </p>
            )}
          </div>
        </div>
        <div
          className="input-container"
          style={{ width: isMobile ? "90%" : "46%" }}
        >
          <div className="mb-3 input-div input-div-10">
            <h2 id="email-label" className="form-label">
              Email
            </h2>
            <input
              style={{ marginTop: "1rem" }}
              className="form-control input-5"
              id="email"
              name="email"
              value={formData.email}
              aria-labelledby="email-label"
              placeholder="email"
              onChange={handleChange}
            ></input>
          </div>
        </div>
        <div
          className="input-container"
          style={{ width: isMobile ? "90%" : "46%", gap: "0.2rem" }}
        >
          <h2 id="social-media-label" className="form-label">
            Social Media
          </h2>

          {/* Render dynamic input fields */}
          {formData.social.map((socialLink: any, index: any) => (
            <div key={index} className="mb-0 input-div">
              <input
                style={{ marginTop: "1rem" }}
                className="form-control input-5"
                id={`social-input-${index}`}
                name={`social-media-${index}`}
                aria-label={
                  index === 0
                    ? "LinkedIn"
                    : index === 1
                    ? "Twitter"
                    : "Social link"
                }
                value={socialLink}
                placeholder={
                  index === 0
                    ? "LinkedIn"
                    : index === 1
                    ? "Twitter"
                    : "Social link"
                }
                onChange={(e) => handleSocialChange(index, e.target.value)}
              />
            </div>
          ))}

          {/* Button to add new input */}
          <button
            type="button"
            className="btn add-social"
            onClick={addSocialInput}
            style={{ marginTop: "1rem" }}
          >
            Add more
          </button>
        </div>
      </form>
      {submitError && <p className="page-error" role="alert">{submitError}</p>}
      <div
        style={{ width: "100%", paddingTop: "2rem" }}
        className="d-flex items-center justify-center"
      >
        <button
          type="submit"
          className="doc-submit"
          disabled={isSubmitting || !userDetails}
          form="myForm"
          aria-describedby={isSubmitting ? "profile-update-status" : undefined}
        >
          {isSubmitting
            ? (formData.resume ? "Uploading resume…" : "Updating profile…")
            : formData.resume
              ? "Save resume and profile"
              : "Update profile"}
        </button>
      </div>
      <Modal
        show={showDeleteResumeConfirmation}
        onHide={closeDeleteResumeConfirmation}
        centered
        aria-labelledby="delete-resume-confirmation-title"
        aria-describedby="delete-resume-confirmation-description"
        backdrop={isDeletingResume ? "static" : true}
        keyboard={!isDeletingResume}
        onExited={handleDeleteConfirmationExited}
      >
        <Modal.Header closeButton={!isDeletingResume}>
          <Modal.Title as="h2" id="delete-resume-confirmation-title">
            Delete saved resume?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p id="delete-resume-confirmation-description">
            This permanently deletes the resume and the profile information extracted from it for Career Discovery. This action cannot be undone.
          </p>
          {deleteResumeError && <p className="page-error" role="alert">{deleteResumeError}</p>}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="profile-resume-cancel-delete"
            onClick={closeDeleteResumeConfirmation}
            disabled={isDeletingResume}
          >
            Cancel
          </button>
          <button
            type="button"
            className="profile-resume-confirm-delete"
            onClick={deleteSavedResume}
            disabled={isDeletingResume}
            aria-busy={isDeletingResume}
          >
            {isDeletingResume ? "Deleting resume…" : "Delete resume"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditProfileForm;
